import React from 'react';
import { PageSection, CustomSectionContent } from '../../types/page';

/**
 * CustomSection Component
 *
 * A flexible and reusable section component for building custom layouts in the landing page.
 * Supports multiple layout variants, optional backgrounds, and children components.
 *
 * This component is designed to work with the PageSection system used in the CMS.
 *
 * @example
 * // Used through SectionRenderer with PageSection data
 * <CustomSection section={sectionData} />
 *
 * The section.content can include:
 * - variant: 'default' | 'centered' | 'split'
 * - backgroundColor: Tailwind bg class (e.g., 'bg-blue-600')
 * - textColor: Tailwind text class (e.g., 'text-white')
 * - backgroundImage: URL to background image
 * - showOverlay: boolean for overlay on background image
 * - padding: 'none' | 'sm' | 'md' | 'lg' | 'xl'
 * - containerWidth: 'sm' | 'md' | 'lg' | 'xl' | 'full'
 * - htmlContent: Optional HTML string to render
 */

interface CustomSectionProps {
  section: PageSection;
  className?: string;
}

export const CustomSection: React.FC<CustomSectionProps> = ({
  section,
  className = ''
}) => {
  const content = section.content as CustomSectionContent;

  // Extract configuration from content with defaults
  const variant = content.variant || 'default';
  const backgroundColor = content.backgroundColor || 'bg-white';
  const textColor = content.textColor || 'text-gray-900';
  const backgroundImage = content.backgroundImage;
  const showOverlay = content.showOverlay !== undefined ? content.showOverlay : true;
  const padding = content.padding || 'lg';
  const containerWidth = content.containerWidth || 'lg';
  // Padding classes based on size
  const paddingClasses = {
    none: 'py-0',
    sm: 'py-8 md:py-12',
    md: 'py-12 md:py-16',
    lg: 'py-16 md:py-20',
    xl: 'py-20 md:py-28'
  };

  // Container width classes
  const containerClasses = {
    sm: 'max-w-3xl',
    md: 'max-w-5xl',
    lg: 'max-w-7xl',
    xl: 'max-w-[1400px]',
    full: 'max-w-full'
  };

  // Background style for images
  const backgroundStyle = backgroundImage
    ? { backgroundImage: `url(${backgroundImage})` }
    : {};

  // Variant-specific layout classes
  const getVariantClasses = () => {
    switch (variant) {
      case 'centered':
        return 'text-center';
      case 'split':
        return '';
      default:
        return '';
    }
  };

  // Content wrapper classes based on variant
  const getContentWrapperClasses = () => {
    switch (variant) {
      case 'centered':
        return 'max-w-3xl mx-auto';
      case 'split':
        return 'grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center';
      default:
        return '';
    }
  };

  return (
    <section
      id={section.id}
      className={`relative ${backgroundColor} ${paddingClasses[padding]} bg-cover bg-center bg-no-repeat ${className}`}
      style={backgroundStyle}
      aria-label={section.title ? `Section: ${section.title}` : undefined}
    >
      {/* Overlay for background images */}
      {backgroundImage && showOverlay && (
        <div className="absolute inset-0 bg-black/60" aria-hidden="true" />
      )}

      {/* Content Container */}
      <div className={`relative z-10 container mx-auto px-4 ${containerClasses[containerWidth]} ${getVariantClasses()}`}>
        {/* Header */}
        {(section.title || section.subtitle) && (
          <div className={`mb-12 ${variant === 'centered' ? 'text-center' : ''}`}>
            {section.title && (
              <h2 className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-4 ${
                backgroundImage ? 'text-white' : textColor
              }`}>
                {section.title}
              </h2>
            )}

            {section.subtitle && (
              <p className={`text-lg md:text-xl max-w-3xl ${
                variant === 'centered' ? 'mx-auto' : ''
              } ${
                backgroundImage ? 'text-gray-200' : 'text-gray-600'
              }`}>
                {section.subtitle}
              </p>
            )}
          </div>
        )}

        {/* HTML Content */}
        {content.htmlContent && (
          <div
            className={`prose prose-lg max-w-none ${getContentWrapperClasses()} ${
              backgroundImage ? 'prose-invert' : ''
            }`}
            dangerouslySetInnerHTML={{ __html: content.htmlContent }}
          />
        )}

        {/* Fallback: Show content type info if no htmlContent */}
        {!content.htmlContent && (
          <div className={`${getContentWrapperClasses()} text-center`}>
            <p className={`text-lg ${backgroundImage ? 'text-gray-200' : 'text-gray-600'}`}>
              Este es un espacio personalizable. Configure el contenido desde el CMS.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default CustomSection;
